import * as fx from '../audio/fx';
import * as engine from '../app/engine';
import { app, on } from '../app/state';
import { DIAL_MAX, PHASE_LABEL, PHASE_VAR } from '../core/constants';
import * as T from '../core/timer';
import { getDay } from '../core/history';
import { clamp, dayKey, fmtClock, fmtHM } from '../core/util';
import { $, animate, ic, reducedMotion, svgEl } from './dom';
import { hasDial, onLayout } from './layout';
import { toast } from './toast';

const C = 220;
const R_ARC = 160;

const dial = $<SVGSVGElement>('#dial');
const wrap = $('#dialWrap');
const timeText = $('#timeText');
const phaseText = $('#phaseText');
const metaText = $('#metaText');
const hint = $('#hint');
const dialCenter = $('#dialCenter');
const btnStart = $('#btnStart');
const timerStatus = $('#timerStatus');

let arc: SVGCircleElement;
let setArc: SVGCircleElement;
let hand: SVGLineElement;
let handKnob: SVGCircleElement;
let runKnob: SVGCircleElement;
let goalArc: SVGCircleElement;
const hzFill = $('.hz-line i');

/** Other views that mirror the countdown (flip tiles, pop-out mini timer). */
const timeListeners: ((clock: string) => void)[] = [];
export const onTimeRendered = (fn: (clock: string) => void) => timeListeners.push(fn);

let dragging = false;
let dragMin = 0;
let lastSec = -1;

const run = () => app.data.run;
const settings = () => app.data.settings;

function buildDial() {
  const g = svgEl('g');
  for (let i = 0; i < DIAL_MAX; i++) {
    const a = ((i * 360) / DIAL_MAX - 90) * (Math.PI / 180);
    const maj = i % 5 === 0;
    const r1 = maj ? 176 : 181;
    g.appendChild(
      svgEl('line', {
        x1: C + r1 * Math.cos(a),
        y1: C + r1 * Math.sin(a),
        x2: C + 196 * Math.cos(a),
        y2: C + 196 * Math.sin(a),
        class: maj ? 'tk maj' : 'tk',
      }),
    );
  }
  dial.appendChild(g);
  const gn = svgEl('g');
  for (const m of [15, 30, 45, 60, 75]) {
    const a = ((m / DIAL_MAX) * 360 - 90) * (Math.PI / 180);
    const t = svgEl('text', {
      x: C + 208 * Math.cos(a),
      y: C + 208 * Math.sin(a) + 4,
      class: 'dnum',
      'text-anchor': 'middle',
    });
    t.textContent = String(m);
    gn.appendChild(t);
  }
  dial.appendChild(gn);
  const ring = { cx: C, cy: C, r: R_ARC };
  const arcAttrs = {
    ...ring,
    class: 'arc',
    pathLength: 360,
    'stroke-dasharray': 360,
    transform: `rotate(-90 ${C} ${C})`,
  };
  dial.appendChild(svgEl('circle', { ...ring, class: 'track' }));
  setArc = dial.appendChild(svgEl('circle', { ...arcAttrs, id: 'setArc', 'stroke-dashoffset': 360 }));
  arc = dial.appendChild(svgEl('circle', { ...arcAttrs, id: 'arc', 'stroke-dashoffset': 0 }));
  hand = dial.appendChild(svgEl('line', { id: 'hand', class: 'hand' }));
  handKnob = dial.appendChild(svgEl('circle', { id: 'handKnob', r: 8, class: 'handKnob' }));
  runKnob = dial.appendChild(svgEl('circle', { id: 'runKnob', r: 8, class: 'runKnob' }));
  // Logbook: today's sessions against the daily goal, as a ring just outside the ticks
  const goalRing = { cx: C, cy: C, r: 205 };
  dial.appendChild(svgEl('circle', { ...goalRing, class: 'goal-track' }));
  goalArc = dial.appendChild(
    svgEl('circle', {
      ...goalRing,
      class: 'goal-arc',
      pathLength: 360,
      'stroke-dasharray': 360,
      'stroke-dashoffset': 360,
      transform: `rotate(-90 ${C} ${C})`,
    }),
  );
}

function renderGoalRing() {
  const goal = settings().dailyGoal;
  const done = getDay(app.data.history, dayKey(Date.now())).sessions;
  const f = goal ? Math.min(1, done / goal) : 0;
  goalArc.setAttribute('stroke-dashoffset', (360 * (1 - f)).toFixed(2));
  goalArc.classList.toggle('met', goal > 0 && done >= goal);
}

const polar = (frac: number, r: number) => {
  const a = (frac * 360 - 90) * (Math.PI / 180);
  return [C + r * Math.cos(a), C + r * Math.sin(a)] as const;
};

export function setHandGeom(m: number) {
  const f = m / DIAL_MAX;
  const [x1, y1] = polar(f, 62);
  const [x2, y2] = polar(f, 146);
  const [kx, ky] = polar(f, R_ARC);
  hand.setAttribute('x1', String(x1));
  hand.setAttribute('y1', String(y1));
  hand.setAttribute('x2', String(x2));
  hand.setAttribute('y2', String(y2));
  handKnob.setAttribute('cx', String(kx));
  handKnob.setAttribute('cy', String(ky));
}
export const setArcGeom = (m: number) =>
  setArc.setAttribute('stroke-dashoffset', String(360 - (m / DIAL_MAX) * 360));

/** The straight progress line used by Horizon, Flip and Strip (fills as time passes). */
const paintLine = (fracLeft: number) => (hzFill.style.transform = `scaleX(${(1 - fracLeft).toFixed(4)})`);

function paintProgress(frac: number) {
  paintLine(frac);
  arc.setAttribute('stroke-dashoffset', (360 * (1 - frac)).toFixed(2));
  const [x, y] = polar(frac, R_ARC);
  runKnob.setAttribute('cx', String(x));
  runKnob.setAttribute('cy', String(y));
}

const show = (els: SVGElement[], on: boolean) => els.forEach((e) => e.classList.toggle('is-hidden', !on));

const canSetFocus = () => !run().running && run().phase === 'focus';

function renderDial() {
  const showHand = canSetFocus() || dragging;
  show([hand, handKnob, setArc], showHand);
  show([arc, runKnob], !showHand);
  if (showHand && !dragging) {
    setHandGeom(settings().focus);
    setArcGeom(settings().focus);
    paintLine(1);
  }
  if (!showHand) paintProgress(T.fractionLeft(run(), Date.now()));
}

function renderTime(ms: number) {
  const s = fmtClock(ms);
  timeText.innerHTML = '';
  timeText.append(
    s.slice(0, 2),
    Object.assign(document.createElement('span'), { className: 'colon', textContent: ':' }),
    s.slice(3),
  );
  timeListeners.forEach((fn) => fn(s));
}

function stateName(): 'running' | 'paused' | 'idle' {
  const r = run();
  return r.running ? 'running' : r.remaining < r.total ? 'paused' : 'idle';
}

function renderMeta() {
  if (dragging) return void (metaText.textContent = 'RELEASE TO SET');
  const st = stateName();
  metaText.textContent =
    st === 'running' ? `ENDS ${fmtHM(run().endAt)}` : st === 'paused' ? 'PAUSED' : 'READY';
}

/** Also driven by the background heartbeat: animation frames stop in hidden tabs, the tab title must not. */
export function renderTitle() {
  const st = stateName();
  const left = fmtClock(T.remainingAt(run(), Date.now()));
  document.title =
    st === 'running'
      ? `${left} · ${PHASE_LABEL[run().phase]} — Pomodoro`
      : st === 'paused'
        ? `${left} · paused — Pomodoro`
        : 'Pomodoro · Focus Instrument';
}

function renderControls() {
  const st = stateName();
  btnStart.innerHTML =
    st === 'running'
      ? `${ic('pause')}<span>PAUSE</span>`
      : `${ic('play')}<span>${st === 'paused' ? 'RESUME' : 'START'}</span>`;
  const canDial = st === 'idle' && run().phase === 'focus';
  const setHow = hasDial() ? 'DRAG THE DIAL TO SET FOCUS' : 'PICK A RHYTHM TO SET FOCUS';
  // Touch screens get no keyboard hints (an empty hint collapses via CSS).
  hint.textContent = touchOnly.matches
    ? canDial
      ? setHow
      : ''
    : st === 'running'
      ? 'SPACE TO PAUSE · R RESET · S SKIP · F ZEN'
      : st === 'paused'
        ? 'SPACE TO RESUME · R RESET · S SKIP'
        : canDial
          ? `${setHow} · SPACE TO START`
          : 'SPACE TO START YOUR BREAK · S TO SKIP IT';
}
const touchOnly = matchMedia('(hover: none) and (pointer: coarse)');

function renderSlider() {
  const m = dragging ? dragMin : settings().focus;
  wrap.setAttribute('aria-valuenow', String(m));
  wrap.setAttribute('aria-valuetext', `${m} minutes focus`);
  wrap.setAttribute('aria-disabled', String(!canSetFocus()));
  wrap.setAttribute(
    'aria-label',
    canSetFocus() ? 'Focus length' : `${PHASE_LABEL[run().phase].toLowerCase()} timer, ${stateName()}`,
  );
}

function renderStatus() {
  const left = Math.ceil(T.remainingAt(run(), Date.now()) / 60000);
  timerStatus.textContent = `${PHASE_LABEL[run().phase]}: ${left} minute${left === 1 ? '' : 's'} left, ${stateName()}`;
}

let lastFilled = -1;
function renderRounds() {
  const n = settings().interval;
  const r = run();
  const filled = r.phase === 'long' ? n : r.round - 1;
  const el = $('#dots');
  el.innerHTML = '';
  for (let i = 0; i < n; i++) {
    const d = document.createElement('i');
    if (i < filled) d.className = 'done';
    else if (i === filled && r.phase !== 'long') d.className = 'cur';
    if (i === filled - 1 && filled > lastFilled && !reducedMotion()) d.classList.add('flash');
    el.appendChild(d);
  }
  lastFilled = filled;
  $('#roundLbl').textContent =
    r.phase === 'long' ? 'RECOVERY CYCLE' : `ROUND ${Math.min(r.round, n)} OF ${n}`;
}

function renderPills() {
  const d = app.data;
  const el = $('#nowTask');
  const t = d.tasks.find((x) => x.id === d.activeTaskId && !x.done);
  if (!t) el.hidden = true;
  else {
    $('#ntText').textContent = t.text;
    $('#ntProg').innerHTML = Array.from(
      { length: t.est },
      (_, i) => `<i class="${i < t.count ? 'f' : ''}"></i>`,
    ).join('');
    el.setAttribute('aria-label', `Now: ${t.text}, ${t.count} of ${t.est} pomodoros — open tasks`);
    if (el.hidden) {
      el.hidden = false;
      animate(
        el,
        [
          { opacity: 0, transform: 'translateY(6px) scale(.95)' },
          { opacity: 1, transform: 'none' },
        ],
        {
          duration: 350,
          easing: 'cubic-bezier(.34,1.45,.5,1)',
        },
      );
    }
  }
  const r = d.run;
  const ib = $('#intBtn');
  ib.hidden = !(r.phase === 'focus' && T.inProgress(r));
  $('#intCount').textContent = String(r.interruptions);
  ib.setAttribute('aria-label', `Log an interruption (${r.interruptions} so far)`);
}

function applyPhaseColor() {
  document.documentElement.style.setProperty('--phase', `var(${PHASE_VAR[run().phase]})`);
  phaseText.textContent = PHASE_LABEL[run().phase];
}

export function paintAll() {
  const st = stateName();
  document.body.classList.remove('idle', 'running', 'paused');
  document.body.classList.add(st);
  applyPhaseColor();
  renderDial();
  renderTime(dragging ? dragMin * 60000 : T.remainingAt(run(), Date.now()));
  renderControls();
  renderMeta();
  renderTitle();
  renderRulerMark();
  renderSlider();
  renderRounds();
  renderPills();
  renderStatus();
}

function phaseSwap() {
  dialCenter.classList.remove('swap');
  void dialCenter.offsetWidth;
  if (!reducedMotion()) dialCenter.classList.add('swap');
}

function wash() {
  if (reducedMotion()) return;
  const c = getComputedStyle(document.documentElement).getPropertyValue(PHASE_VAR[run().phase]).trim();
  const w = $('#wash');
  w.style.background = c;
  w.animate([{ opacity: '0.16' }, { opacity: '0' }], { duration: 1000, easing: 'cubic-bezier(.16,.8,.3,1)' });
}

/* ---------- per-frame loop ---------- */
function frame() {
  engine.tick();
  const r = run();
  if (r.running) {
    const t = Date.now();
    paintProgress(T.fractionLeft(r, t));
    const left = T.remainingAt(r, t);
    const s = Math.ceil(left / 1000);
    if (s !== lastSec) {
      lastSec = s;
      renderTime(left);
      renderTitle();
      if (s % 30 === 0) renderStatus();
      if (settings().ticking && r.phase === 'focus') fx.tick();
    }
  }
  sideFrame();
  requestAnimationFrame(frame);
}
let sideFrame: () => void = () => {};

/* ---------- dial: pointer drag ---------- */
function angleMinutes(e: PointerEvent) {
  const r = wrap.getBoundingClientRect();
  const x = e.clientX - (r.left + r.width / 2);
  const y = e.clientY - (r.top + r.height / 2);
  let deg = (Math.atan2(y, x) * 180) / Math.PI + 90;
  if (deg < 0) deg += 360;
  return clamp(Math.round(deg / (360 / DIAL_MAX)), 1, DIAL_MAX);
}

function applyDrag(e: PointerEvent) {
  const m = angleMinutes(e);
  if (m === dragMin) return;
  dragMin = m;
  fx.tick();
  fx.haptic();
  setHandGeom(m);
  setArcGeom(m);
  renderRulerMark(m);
  renderTime(m * 60000);
  metaText.textContent = 'RELEASE TO SET';
}

function endDrag() {
  if (!dragging) return;
  dragging = false;
  wrap.classList.remove('dragging');
  const changed = dragMin !== settings().focus;
  phaseText.textContent = PHASE_LABEL[run().phase];
  if (changed) {
    engine.setFocusMinutes(dragMin);
    toast(`Focus set to ${dragMin} min`);
  } else paintAll();
}

function bindDial() {
  wrap.addEventListener('pointerdown', (e) => {
    fx.ensureAudio();
    // no dial face in Horizon / Flip / Strip: the timer is still a keyboard slider there
    if (!hasDial() || !canSetFocus() || e.button) return;
    dragging = true;
    dragMin = settings().focus;
    wrap.classList.add('dragging');
    wrap.setPointerCapture(e.pointerId);
    show([hand, handKnob, setArc], true);
    show([arc, runKnob], false);
    phaseText.textContent = 'SET FOCUS';
    applyDrag(e);
  });
  wrap.addEventListener('pointermove', (e) => dragging && applyDrag(e));
  wrap.addEventListener('pointerup', endDrag);
  wrap.addEventListener('pointercancel', endDrag);

  // keyboard slider
  wrap.addEventListener('keydown', (e) => {
    if (!canSetFocus()) return;
    const cur = settings().focus;
    const step: Record<string, number> = {
      ArrowUp: 1,
      ArrowRight: 1,
      ArrowDown: -1,
      ArrowLeft: -1,
      PageUp: 5,
      PageDown: -5,
    };
    let next: number | null = null;
    if (e.key in step) next = cur + step[e.key]!;
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = DIAL_MAX;
    if (next === null) return;
    e.preventDefault();
    e.stopPropagation();
    next = clamp(next, 1, DIAL_MAX);
    if (next !== cur) {
      fx.tick();
      engine.setFocusMinutes(next);
    }
  });
}

/* ---------- ruler ---------- */
const strip = $('#rlStrip');
function buildRuler() {
  for (let m = DIAL_MAX; m >= 0; m -= 15) {
    const s = document.createElement('span');
    s.className = 'rl-n';
    s.style.top = `${(1 - m / DIAL_MAX) * 100}%`;
    s.textContent = String(m);
    strip.appendChild(s);
  }
}
export function renderRulerMark(m?: number) {
  const v = m ?? settings().focus;
  $('#rulerMark').style.top = `${(1 - v / DIAL_MAX) * 100}%`;
  $('#rmVal').textContent = String(v);
}
function bindRuler() {
  let active = false;
  const apply = (e: PointerEvent) => {
    const r = strip.getBoundingClientRect();
    const m = clamp(Math.round((1 - (e.clientY - r.top) / r.height) * DIAL_MAX), 1, DIAL_MAX);
    if (m !== settings().focus) {
      fx.tick();
      engine.setFocusMinutes(m);
    }
  };
  strip.addEventListener('pointerdown', (e) => {
    if (!canSetFocus()) return;
    active = true;
    strip.setPointerCapture(e.pointerId);
    apply(e);
  });
  strip.addEventListener('pointermove', (e) => active && apply(e));
  for (const ev of ['pointerup', 'pointercancel']) strip.addEventListener(ev, () => (active = false));
}

/* ---------- init ---------- */
export function initTimerView(opts: { sideFrame: () => void }) {
  sideFrame = opts.sideFrame;
  buildDial();
  buildRuler();
  bindDial();
  bindRuler();

  $('#btnStart').addEventListener('click', () => {
    fx.tap();
    engine.toggle();
  });
  $('#btnReset').addEventListener('click', () => {
    fx.tap();
    engine.reset();
  });
  $('#btnSkip').addEventListener('click', () => engine.skip());
  $('#intBtn').addEventListener('click', () => engine.logInterruption());

  engine.viewHooks.wash = wash;
  engine.viewHooks.phaseChanged = phaseSwap;
  on('run', () => {
    lastSec = -1;
    paintAll();
  });
  on('settings', paintAll);
  on('tasks', renderPills);
  on('stats', renderGoalRing);
  on('settings', renderGoalRing);
  onLayout(() => {
    lastSec = -1;
    paintAll();
  });
  renderGoalRing();

  const stamp = () =>
    ($('#dateStamp').textContent = new Date()
      .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
      .replace(',', '')
      .toUpperCase());
  stamp();
  on('stats', stamp); // also fires on the midnight rollover

  paintAll();
  if (canSetFocus() && !reducedMotion()) {
    // sweep the hand into place on load
    const target = settings().focus;
    const t0 = performance.now();
    const step = (t: number) => {
      const k = clamp((t - t0) / 1100, 0, 1);
      const m = target * (1 - Math.pow(1 - k, 3));
      if (!dragging && canSetFocus()) {
        setHandGeom(m);
        setArcGeom(m);
      }
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
  requestAnimationFrame(frame);
}
